/* eslint-disable max-lines */
import React, { useEffect, useRef, useState } from 'react';
import { copyToClipboard } from '../../helpers/utils';
import { ActionButton } from '../ActionButton';
import { Preview } from '../Preview';
import TypingEffect from '../TypingEffect';
import RobotWithPulse from '../RobotWithPulse';
import {
  ContentContainer,
  EmptyStateContainer,
  StyledMarkdown,
  LoadingMessageContainer,
  ResponseTime,
  Answer,
  BotAnswer,
  MessageBlock,
  UserQuestion,
  ChatHistory,
  PreviewContainer,
  WalkthroughContainer,
  WalkthroughLink,
} from './styles';
import { Attachment } from '../Attachment';
import { Image } from '../Image';
import { Audio } from '../Audio';
import { Tool } from 'models/chatbot';
import useChatbot from 'hooks/useChatbot';
import FeedbackModal from '../FeedbackModal';
import { useDispatch } from 'react-redux';
import actions from 'state/actions';
import utils from 'utils';
import { CommandChips } from '../CommandChips';

interface ContentProps {
  loading: boolean;
  loadingMessage: string;
  tools: Tool[];
  setQuestion: (question: string) => void;
  handleInputChange: (value: string, tools: Tool[]) => void;
  inputRef: React.RefObject<HTMLInputElement>;
  selectCommand: (commandName: string) => string;
}

const Content: React.FC<ContentProps> = ({
  loading,
  loadingMessage,
  tools,
  setQuestion,
  handleInputChange,
  inputRef,
  selectCommand,
}) => {
  const dispatch = useDispatch();
  const t = utils.intl.useTranslator();

  const chatHistoryRef = useRef<HTMLDivElement>(null);

  const { ask, messages, mode, sendFeedback, feedbackStates } = useChatbot();
  const messagesRef = useRef<boolean | null>(null);

  useEffect(() => {
    // Check if we have a new message
    const hasNewMessage = messagesRef.current;
    messagesRef.current = messages.length > 0;

    if (chatHistoryRef.current) {
      const chatContainer = chatHistoryRef.current;

      setTimeout(() => {
        if (chatContainer) {
          chatContainer.scrollTo({
            top: chatContainer.scrollHeight,
            behavior: hasNewMessage ? 'smooth' : 'auto',
          });
        }
      }, 50);
    }
  }, [messages]);

  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackMessageId, setFeedbackMessageId] = useState('');

  const handleFeedback = async ({
    rating,
    message,
    chatHistoryId,
  }: {
    rating?: number;
    message?: string;
    chatHistoryId: string;
  }) => {
    sendFeedback({ rating, chatHistoryId, message });
    setIsFeedbackModalOpen(false);
    setFeedbackMessage('');
  };

  return (
    <>
      <ContentContainer>
        {messages.length === 0 && !loading ? (
          <EmptyStateContainer>
            <RobotWithPulse size={mode === 'embedded' ? 350 : 280} />
          </EmptyStateContainer>
        ) : (
          <ChatHistory ref={chatHistoryRef}>
            {messages.map((msg, index) => (
              <MessageBlock key={index}>
                <UserQuestion>{msg.question}</UserQuestion>
                {msg.answer || (loading && index === messages.length - 1) ? (
                  <BotAnswer>
                    <Answer>
                      {msg.answer ? (
                        <StyledMarkdown>
                          <TypingEffect text={msg.answer} links={msg.links} />
                          {!!msg?.walkthroughFlow?.steps?.length && (
                            <WalkthroughContainer>
                              {utils.app.textTranslated(
                                'walkthroughtGuideSuggestionText',
                                t,
                                'If you\u2019d like more detailed help',
                              )}{' '}
                              <WalkthroughLink
                                onClick={() =>
                                  msg.walkthroughFlow && dispatch(actions.chatbot.startWalkthrough(msg.walkthroughFlow))
                                }
                              >
                                {utils.app.textTranslated('walkthroughtGuideSuggestionLink', t, 'start a guided tour')}
                              </WalkthroughLink>
                              .
                            </WalkthroughContainer>
                          )}
                          {!!msg?.previews?.length && (
                            <PreviewContainer>
                              {msg.previews.map((preview, index: number) => (
                                <Preview
                                  key={index}
                                  page={preview.page}
                                  imageBase64={preview.image_base64}
                                  width={preview.width}
                                  docSource={preview.link?.url}
                                />
                              ))}
                            </PreviewContainer>
                          )}
                          {!!msg?.images?.length && (
                            <PreviewContainer>
                              {msg.images.map((image, index: number) => (
                                <Image
                                  mimeType={image.mime_type}
                                  url={image.url}
                                  key={index}
                                  imageBase64={image.image_base64}
                                />
                              ))}
                            </PreviewContainer>
                          )}
                          {!!msg?.audios?.length && (
                            <PreviewContainer>
                              {msg.audios.map((audio, index: number) => (
                                <Audio
                                  mimeType={audio.mime_type}
                                  url={audio.url}
                                  audioBase64={audio.audio_base64}
                                  key={index}
                                />
                              ))}
                            </PreviewContainer>
                          )}
                          {!!msg?.attachments?.length && (
                            <div>
                              {msg.attachments.map((attachment, index: number) => (
                                <Attachment
                                  key={index}
                                  contentType={attachment.content_type}
                                  fileBase64={attachment.file_base64}
                                  filename={attachment.filename || ''}
                                />
                              ))}
                            </div>
                          )}
                        </StyledMarkdown>
                      ) : (
                        <LoadingMessageContainer>{loadingMessage}</LoadingMessageContainer>
                      )}
                    </Answer>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', gap: '5px' }}>
                        {msg.answer && <ActionButton buttonType="copy" onClick={() => copyToClipboard(msg.answer)} />}
                        {msg.chatHistoryId && (
                          <>
                            <ActionButton
                              buttonType="thumbUp"
                              onClick={() =>
                                msg.chatHistoryId && handleFeedback({ rating: 1, chatHistoryId: msg.chatHistoryId })
                              }
                              isActive={feedbackStates[msg.chatHistoryId] === 'up'}
                            />
                            <ActionButton
                              buttonType="thumbDown"
                              onClick={() =>
                                msg.chatHistoryId && handleFeedback({ rating: -1, chatHistoryId: msg.chatHistoryId })
                              }
                              isActive={feedbackStates[msg.chatHistoryId] === 'down'}
                            />
                            <ActionButton
                              buttonType="flag"
                              onClick={() => {
                                if (!msg.chatHistoryId) return;
                                setIsFeedbackModalOpen(true);
                                setFeedbackMessageId(msg.chatHistoryId);
                              }}
                              isActive={feedbackStates[msg.chatHistoryId] === 'flag'}
                            />
                          </>
                        )}

                        {msg.answer && <ActionButton buttonType="retry" onClick={() => ask(msg.question)} />}
                      </div>
                      {msg.responseTime && (
                        <ResponseTime>
                          {utils.app.textTranslated('answeredIn', t, 'Answered in')}{' '}
                          {(msg.responseTime / 1000).toFixed(3)} {utils.app.textTranslated('seconds', t, 'seconds')}
                        </ResponseTime>
                      )}
                    </div>
                  </BotAnswer>
                ) : null}
              </MessageBlock>
            ))}
          </ChatHistory>
        )}

        <CommandChips
          tools={tools}
          setQuestion={setQuestion}
          handleInputChange={handleInputChange}
          inputRef={inputRef}
          selectCommand={selectCommand}
        />
      </ContentContainer>

      <FeedbackModal
        isOpen={isFeedbackModalOpen}
        onClose={() => {
          setIsFeedbackModalOpen(false);
          setFeedbackMessage('');
        }}
        message={feedbackMessage}
        setMessage={setFeedbackMessage}
        send={() => handleFeedback({ message: feedbackMessage, chatHistoryId: feedbackMessageId })}
      />
    </>
  );
};

export default Content;
